import {
	Address,
	AddressAutocompleteResponse,
} from "../Models/AddressAutocompleteModel";
import { AddressRequest } from "../Models/AddressModel";

export function toAddressRequestFromLookup(details: Address): AddressRequest | null {
	const requiredFields = [
		details.vejnavn,
		details.husnr,
		details.postnr,
		details.postnrnavn,
		details.kommunekode,
		details.vejkode,
	];
	if (requiredFields.some((value) => !value?.trim())) {
		return null;
	}

	return {
		street: details.vejnavn!.trim(),
		houseNumber: details.husnr!.trim(),
		floor: details.etage?.trim() ?? "",
		door: details.dør?.trim() ?? "",
		postCode: details.postnr!.trim(),
		city: details.postnrnavn!.trim(),
	};
}

export function isCompleteAddressRequest(
	request: Partial<AddressRequest> | null | undefined
): boolean {
	return !!(
		request?.street?.trim() &&
		request.houseNumber?.trim() &&
		request.postCode?.trim() &&
		request.city?.trim()
	);
}

export function isFullAddress(
	type: string,
	data: AddressAutocompleteResponse["data"]
): boolean {
	if (type !== "adresse") {
		return false;
	}
	// Wrapped format: { address: { id: "..." } }
	if ("address" in data && !!(data as { address: Address }).address?.id) {
		return true;
	}
	// Flat format: data IS the Address directly (has `id` field)
	if ("id" in data && !!(data as Address).id) {
		return true;
	}
	return false;
}

export function extractAddress(
	type: string,
	data: AddressAutocompleteResponse["data"]
): Address | null {
	if (type !== "adresse" || !data || typeof data !== "object") {
		return null;
	}
	// Wrapped format
	if ("address" in data && typeof (data as { address: Address }).address?.id === "string" && (data as { address: Address }).address.id.trim()) {
		return (data as { address: Address }).address;
	}
	// Flat format: data itself is an Address
	if ("id" in data && typeof (data as Address).id === "string" && (data as Address).id.trim()) {
		return data as Address;
	}
	return null;
}

export interface AddressValidationResult {
	valid: boolean;
	address?: Address;
}

export const validateAddressResponse = (
	response: AddressAutocompleteResponse
): AddressValidationResult => {
	const address = extractAddress(response.type, response.data);

	if (!address) {
		return { valid: false };
	}

	return { valid: true, address };
};
